/* eslint-disable complexity */
import { OrderCouponContextValues } from '@/components/content/Cart/parts/OrderCoupon';
import { useCouponStatus } from '@/data/Content/CouponStatus';
import { ContentContext } from '@/data/context/content';
import { parseCouponDescription } from '@/utils/parseCouponDescription';
import { parseHTML } from '@/utils/parseHTML';
import CloseIcon from '@mui/icons-material/Close';
import ConfirmationNumberOutlinedIcon from '@mui/icons-material/ConfirmationNumberOutlined';
import KeyboardArrowDownIcon from '@mui/icons-material/KeyboardArrowDown';
import { Box, Collapse, IconButton, Stack, Typography } from '@mui/material';
import { useContext, useState } from 'react';

type OrderCouponRestrictionProps = {
	code: string;
	promotionId?: string;
	shortDescription?: string;
};

export const OrderCouponRestriction: React.FC<OrderCouponRestrictionProps> = ({
	code,
	promotionId,
	shortDescription: initialShortDescription,
}) => {
	const { onPromoCodeRemove, inactivePromotionCodes } = useContext(
		ContentContext
	) as OrderCouponContextValues;
	const inactive = Boolean(inactivePromotionCodes?.includes(code));
	const accentColor = inactive ? '#9E9E9E' : '#5E3582';
	const [open, setOpen] = useState(false);
	const {
		shortDescription: couponStatusShortDescription,
		longDescription: couponStatusLongDescription,
	} = useCouponStatus(promotionId);

	const sections = parseCouponDescription(couponStatusLongDescription);

	const shortDescription = inactive
		? 'Este cupón no aplica a tu carrito'
		: sections.descripcion ||
		  couponStatusShortDescription ||
		  initialShortDescription ||
		  'Cupón aplicado';
	const hasRestrictions = Boolean(sections.restricciones);
	const couponTitle = sections.descripcionCorta ? `${sections.descripcionCorta}: ${code}` : code;

	const handleRemove = () => {
		void onPromoCodeRemove?.(code);
	};

	if (!code) return null;

	return (
		<Box
			sx={{
				width: '100%',
				border: inactive ? '1px solid #C9C9C9' : '1px solid #774A91',
				borderRadius: '3px',
				padding: '5px 7px',
				backgroundColor: inactive ? '#F5F5F5' : 'transparent',
				color: inactive ? '#757575' : 'inherit',
			}}
			data-testid={`coupon-card-${code}`}
		>
			<Stack direction="row" alignItems="center" justifyContent="space-between">
				<Stack direction="row" alignItems="center" spacing={0.5} minWidth={0}>
					<ConfirmationNumberOutlinedIcon sx={{ color: accentColor, fontSize: '14px' }} />
					<Typography
						sx={{
							fontSize: 'var(--font-size-small)',
							fontWeight: 700,
							lineHeight: 1.2,
							color: '#080808',
						}}
					>
						{parseHTML(couponTitle)}
					</Typography>
				</Stack>
				<IconButton
					aria-label={`Eliminar cupón ${code}`}
					size="small"
					onClick={handleRemove}
					sx={{ padding: 0, color: accentColor }}
				>
					<CloseIcon sx={{ fontSize: '16px' }} />
				</IconButton>
			</Stack>
			<Typography
				sx={{
					color: '#080808',
					fontSize: 'var(--font-size-small)',
					lineHeight: 1.25,
				}}
			>
				{parseHTML(shortDescription)}
				{hasRestrictions ? (
					<>
						{' '}
						<Typography
							component="button"
							onClick={() => setOpen((previous) => !previous)}
							sx={{
								border: 0,
								padding: 0,
								margin: 0,
								background: 'transparent',
								cursor: 'pointer',
								color: accentColor,
								fontSize: 'var(--font-size-small)',
								whiteSpace: 'nowrap',
								display: 'inline',
								verticalAlign: 'baseline',
							}}
							data-testid={`coupon-restrictions-toggle-${code}`}
						>
							{open ? 'Ocultar' : 'Ver restricciones'}
						</Typography>
						<KeyboardArrowDownIcon
							sx={{
								fontSize: '1rem',
								color: accentColor,
								transform: open ? 'rotate(180deg)' : 'none',
								transition: 'transform 0.2s',
								verticalAlign: 'middle',
							}}
						/>
					</>
				) : null}
			</Typography>
			{hasRestrictions ? (
				<Collapse in={open} timeout="auto" unmountOnExit>
					<Typography
						sx={{
							fontSize: 'var(--font-size-small)',
							color: inactive ? '#757575' : '#666',
							marginTop: '3px',
							lineHeight: 1.25,
							'& a': { color: 'inherit', textDecoration: 'underline' },
						}}
					>
						{parseHTML(sections.restricciones ?? '')}
					</Typography>
				</Collapse>
			) : null}
		</Box>
	);
};
